/**
 * Theme Switcher Component
 *
 * Allows users to switch between different visual themes.
 * Displays themes in a grid layout with color previews.
 */

import { Card, Space, Typography, Row, Col } from 'antd';
import { BgColorsOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useTheme } from '@contexts/ThemeContext';
import { getThemes } from '@/themes';

const { Title, Text, Paragraph } = Typography;

export default function ThemeSwitcher() {
  const { themeName, setTheme } = useTheme();
  const themes = getThemes();

  return (
    <Card
      title={
        <Space>
          <BgColorsOutlined />
          <span>Color Scheme</span>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Choose your preferred color scheme. Your selection will be saved and applied across all
        pages.
      </Paragraph>

      <Row gutter={[16, 16]}>
        {themes.map((theme) => {
          const isActive = themeName === theme.name;
          return (
            <Col xs={24} sm={12} lg={8} xl={6} key={theme.name}>
              <Card
                hoverable
                onClick={() => setTheme(theme.name as any)}
                size="small"
                style={{
                  cursor: 'pointer',
                  border: isActive ? '2px solid var(--accent-warm)' : '1px solid var(--border-default, #eee)',
                  height: '100%',
                }}
                bodyStyle={{ padding: 12 }}
              >
                {/* Color preview dots */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      backgroundColor: theme.antdTheme.token?.colorPrimary,
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                    title="Primary"
                  />
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      backgroundColor: theme.antdTheme.token?.colorSuccess,
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                    title="Success"
                  />
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      backgroundColor: theme.antdTheme.token?.colorWarning,
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                    title="Warning"
                  />
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      backgroundColor: theme.cssVariables['--layout-content-bg'],
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                    title="Background"
                  />
                </div>

                {/* Theme name and status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Title level={5} style={{ margin: 0, flex: 1 }}>
                    {theme.displayName}
                  </Title>
                  {isActive && (
                    <CheckCircleFilled
                      style={{
                        fontSize: 18,
                        color: 'var(--accent-warm)',
                      }}
                    />
                  )}
                </div>

                <Text type="secondary" style={{ fontSize: 12 }}>
                  {theme.description}
                </Text>
              </Card>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}
